/* src/components/GlobalMovement.jsx */

import React, { useEffect } from "react";
import { animateScroll as scroll } from "react-scroll";
import { NavLink } from "react-router-dom";

import "./GlobalMovementForm.css";
import Footer from "../pages/footer";

export default function GlobalMovement() {
  /* =========================================================
     PAGE LOAD
  ========================================================= */

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* =========================================================
     SCROLL HELPERS
  ========================================================= */

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const scrollToOverview = () => {
    document
      .getElementById("global-movement-overview")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  return (
    <div className="global-movement-page">
      {/* =====================================================
          FLOATING NAVIGATION
      ====================================================== */}

      <div className="global-movement-floating-nav">
        <button
          type="button"
          className="global-movement-floating-button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M5 12.5L10 7.5L15 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          type="button"
          className="global-movement-floating-button"
          onClick={() =>
            scroll.scrollMore(600, {
              smooth: true,
              duration: 1000,
            })
          }
          aria-label="Scroll down"
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M5 7.5L10 12.5L15 7.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="global-movement-hero">
        <div
          className="global-movement-hero-media"
          aria-hidden="true"
        >
          <video
            className="global-movement-hero-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          >
            <source
              src="/assets/GlobalMovement.mp4"
              type="video/mp4"
            />
          </video>

          <div className="global-movement-hero-overlay" />

          <span className="global-movement-hero-grid" />

          <span className="global-movement-hero-glow global-movement-hero-glow-one" />
          <span className="global-movement-hero-glow global-movement-hero-glow-two" />

          <span className="global-movement-hero-orbit global-movement-hero-orbit-one" />
          <span className="global-movement-hero-orbit global-movement-hero-orbit-two" />
        </div>

        <div className="global-movement-hero-container">
          <div className="global-movement-hero-content">
            <div className="global-movement-eyebrow">
              <span className="global-movement-eyebrow-line" />

              <span>
                GLOBAL DIGITAL &amp; IMMERSIVE LEARNING
              </span>
            </div>

            <h1>
              Expanding access to
              <span> safer learning worldwide.</span>
            </h1>

            <p>
              A global network advancing access to digital
              technologies, immersive learning, and trusted
              occupational health and safety education across
              industries, regions, and communities.
            </p>

            <div className="global-movement-hero-actions">
              <button
                type="button"
                className="global-movement-primary-button"
                onClick={scrollToOverview}
              >
                <span>
                  Explore the Initiative
                </span>

                <span
                  className="global-movement-button-icon"
                  aria-hidden="true"
                >
                  ↓
                </span>
              </button>

              <a
                href="/assets/GlobalDigitalandImmersiveTaskForceConcept.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="global-movement-secondary-link"
              >
                <span>
                  View Task Force Concept
                </span>

                <span aria-hidden="true">
                  ↗
                </span>
              </a>
            </div>
          </div>

          {/* =================================================
              HERO INFORMATION CARD
          ================================================== */}

          <aside className="global-movement-hero-card">
            <div className="global-movement-hero-card-top">
              <span className="global-movement-status">
                <span />
                GLOBAL INITIATIVE
              </span>

              <span className="global-movement-hero-card-number">
                2030
              </span>
            </div>

            <div className="global-movement-hero-card-copy">
              <span>
                A SHARED FRAMEWORK
              </span>

              <h2>
                Connecting technology,
                research, governance,
                and accessibility.
              </h2>

              <p>
                The initiative is designed to connect
                international stakeholders around a
                collaborative model for digital and
                immersive OHS learning.
              </p>
            </div>

            <div className="global-movement-hero-tags">
              <span>
                Innovation
              </span>

              <span>
                Governance
              </span>

              <span>
                Accessibility
              </span>
            </div>
          </aside>
        </div>

        <div
          className="global-movement-hero-line"
          aria-hidden="true"
        />
      </section>

      {/* =====================================================
          MAIN CONTENT
      ====================================================== */}

      <main className="global-movement-main">
        {/* ===================================================
            INTRO METRICS / VALUES
        ==================================================== */}

        <section className="global-movement-principles">
          <div className="global-movement-principles-container">
            <div className="global-movement-principle">
              <span className="global-movement-principle-index">
                01
              </span>

              <div>
                <strong>
                  Global collaboration
                </strong>

                <p>
                  Bringing together expertise
                  across sectors, regions,
                  research, and practice.
                </p>
              </div>
            </div>

            <div className="global-movement-principle-divider" />

            <div className="global-movement-principle">
              <span className="global-movement-principle-index">
                02
              </span>

              <div>
                <strong>
                  Responsible innovation
                </strong>

                <p>
                  Advancing digital learning
                  through evidence, ethics,
                  governance, and validation.
                </p>
              </div>
            </div>

            <div className="global-movement-principle-divider" />

            <div className="global-movement-principle">
              <span className="global-movement-principle-index">
                03
              </span>

              <div>
                <strong>
                  Equitable access
                </strong>

                <p>
                  Expanding the reach of
                  effective safety learning
                  regardless of geography.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            OVERVIEW
        ==================================================== */}

        <section
          id="global-movement-overview"
          className="global-movement-overview"
        >
          <div
            className="global-movement-overview-background"
            aria-hidden="true"
          >
            <span className="global-movement-background-grid" />
            <span className="global-movement-overview-glow" />
          </div>

          <div className="global-movement-section-container">
            <div className="global-movement-section-heading">
              <div>
                <div className="global-movement-section-kicker">
                  <span />

                  <p>
                    THE INITIATIVE
                  </p>
                </div>

                <h2>
                  A shared global
                  <span> movement.</span>
                </h2>
              </div>

              <p className="global-movement-section-intro">
                The Global Digital and Immersive Learning
                Task Force represents a unified effort to
                modernize occupational health and safety
                education through digital and immersive
                learning innovation.
              </p>
            </div>

            <div className="global-movement-overview-grid">
              <div className="global-movement-overview-image-card">
                <div className="global-movement-image-frame">
                  <img
                    src="/assets/Asset2Globe.jpg"
                    alt="Global Digital and Immersive Learning Task Force"
                  />

                  <div className="global-movement-image-overlay" />

                  <div className="global-movement-image-label">
                    <span>
                      GDILTF
                    </span>

                    <strong>
                      Global collaboration
                    </strong>
                  </div>
                </div>
              </div>

              <div className="global-movement-overview-copy">
                <span className="global-movement-copy-number">
                  01
                </span>

                <h3>
                  Built around collaboration,
                  inclusivity, and ethical governance.
                </h3>

                <p>
                  The initiative bridges technology,
                  research, and regulation to create a
                  harmonized and equitable model for
                  workforce learning and safety
                  development.
                </p>

                <p>
                  Its purpose is not simply to introduce
                  new technology, but to establish a
                  trusted framework through which digital
                  and immersive safety learning can be
                  developed, validated, adapted, and
                  implemented across different contexts.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            OPERATING MODEL
        ==================================================== */}

        <section className="global-movement-functions-section">
          <div className="global-movement-section-container">
            <header className="global-movement-functions-header">
              <div>
                <div className="global-movement-section-kicker">
                  <span />

                  <p>
                    OPERATING MODEL
                  </p>
                </div>

                <h2>
                  Four functions.
                  <span> One connected system.</span>
                </h2>
              </div>

              <p>
                The proposed framework connects global,
                regional, and functional leadership
                through four core operational functions
                that drive practical development,
                validation, and implementation.
              </p>
            </header>

            <div className="global-movement-functions-grid">
              <article className="global-movement-function-card">
                <div className="global-movement-function-card-top">
                  <span>
                    01
                  </span>

                  <span className="global-movement-function-dot" />
                </div>

                <h3>
                  Innovation &amp; Collaborative
                  Technology Exchange
                </h3>

                <p>
                  Supports the exchange of emerging
                  technologies, applied knowledge,
                  ideas, and collaborative
                  development opportunities.
                </p>
              </article>

              <article className="global-movement-function-card">
                <div className="global-movement-function-card-top">
                  <span>
                    02
                  </span>

                  <span className="global-movement-function-dot" />
                </div>

                <h3>
                  Regulation &amp;
                  Data Management
                </h3>

                <p>
                  Establishes structures for responsible
                  governance, regulatory alignment,
                  information management, and ethical
                  digital practices.
                </p>
              </article>

              <article className="global-movement-function-card">
                <div className="global-movement-function-card-top">
                  <span>
                    03
                  </span>

                  <span className="global-movement-function-dot" />
                </div>

                <h3>
                  Research &amp;
                  Validation
                </h3>

                <p>
                  Supports evidence-based evaluation,
                  testing, research collaboration,
                  and validation of emerging learning
                  approaches.
                </p>
              </article>

              <article className="global-movement-function-card">
                <div className="global-movement-function-card-top">
                  <span>
                    04
                  </span>

                  <span className="global-movement-function-dot" />
                </div>

                <h3>
                  Product Accessibility &amp;
                  Quality Assurance
                </h3>

                <p>
                  Focuses on usability, accessibility,
                  quality, and adaptation so solutions
                  can serve diverse industries,
                  regions, and workforces.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* ===================================================
            STAKEHOLDER COLLABORATION
        ==================================================== */}

        <section className="global-movement-collaboration">
          <div className="global-movement-section-container">
            <div className="global-movement-collaboration-grid">
              <div className="global-movement-collaboration-copy">
                <div className="global-movement-section-kicker">
                  <span />

                  <p>
                    STAKEHOLDER-DRIVEN
                  </p>
                </div>

                <h2>
                  Participants are not
                  passive advisors.
                  <span> They help build it.</span>
                </h2>

                <p>
                  The GDILTF emphasizes active
                  participation in both innovation and
                  implementation. Stakeholders contribute
                  through innovation roundtables,
                  regional consultations, pilot projects,
                  and the Global Knowledge Exchange
                  Platform.
                </p>

                <p>
                  This continuous engagement is intended
                  to ensure that outcomes remain
                  practical, evidence-based, relevant,
                  and capable of scaling globally.
                </p>

                <div className="global-movement-collaboration-tags">
                  <span>
                    Roundtables
                  </span>

                  <span>
                    Regional consultation
                  </span>

                  <span>
                    Pilot projects
                  </span>

                  <span>
                    Knowledge exchange
                  </span>
                </div>
              </div>

              <div className="global-movement-collaboration-visual">
                <div className="global-movement-framework-image">
                  <img
                    src="/assets/Asset1-Globe1.jpg"
                    alt="GDILTF global framework"
                  />

                  <div className="global-movement-framework-overlay" />
                </div>

                <div className="global-movement-framework-caption">
                  <span>
                    GLOBAL FRAMEWORK
                  </span>

                  <strong>
                    Co-developed across
                    regions and disciplines
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            ROADMAP
        ==================================================== */}

        <section className="global-movement-roadmap">
          <div
            className="global-movement-roadmap-background"
            aria-hidden="true"
          >
            <span className="global-movement-roadmap-glow" />
          </div>

          <div className="global-movement-section-container">
            <header className="global-movement-roadmap-header">
              <div className="global-movement-section-kicker global-movement-section-kicker-centered">
                <span />

                <p>
                  2025 — 2030
                </p>
              </div>

              <h2>
                From consultation
                <span> to global scale.</span>
              </h2>

              <p>
                A phased roadmap supports co-development,
                validation, implementation, and
                long-term institutionalization.
              </p>
            </header>

            <div className="global-movement-roadmap-track">
              <div className="global-movement-roadmap-line" />

              <article className="global-movement-roadmap-item">
                <span className="global-movement-roadmap-marker">
                  01
                </span>

                <div>
                  <span>
                    FOUNDATION
                  </span>

                  <h3>
                    Stakeholder consultation
                  </h3>

                  <p>
                    Engage key stakeholders and establish
                    common priorities, needs, and
                    governance considerations.
                  </p>
                </div>
              </article>

              <article className="global-movement-roadmap-item">
                <span className="global-movement-roadmap-marker">
                  02
                </span>

                <div>
                  <span>
                    REFINEMENT
                  </span>

                  <h3>
                    Framework development
                  </h3>

                  <p>
                    Refine operational structures,
                    governance, strategy, and
                    collaborative mechanisms.
                  </p>
                </div>
              </article>

              <article className="global-movement-roadmap-item">
                <span className="global-movement-roadmap-marker">
                  03
                </span>

                <div>
                  <span>
                    VALIDATION
                  </span>

                  <h3>
                    Pilot implementation
                  </h3>

                  <p>
                    Test emerging approaches through
                    pilot activity, research,
                    adaptation, and evaluation.
                  </p>
                </div>
              </article>

              <article className="global-movement-roadmap-item">
                <span className="global-movement-roadmap-marker">
                  04
                </span>

                <div>
                  <span>
                    SCALE
                  </span>

                  <h3>
                    Global institutionalization
                  </h3>

                  <p>
                    Expand validated models and support
                    long-term adoption across regions,
                    sectors, and partner networks.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* ===================================================
            VISION
        ==================================================== */}

        <section className="global-movement-vision">
          <div className="global-movement-section-container">
            <div className="global-movement-vision-card">
              <div
                className="global-movement-vision-decoration"
                aria-hidden="true"
              >
                <span className="global-movement-vision-orbit global-movement-vision-orbit-one" />
                <span className="global-movement-vision-orbit global-movement-vision-orbit-two" />
                <span className="global-movement-vision-glow" />
              </div>

              <div className="global-movement-vision-content">
                <div className="global-movement-section-kicker">
                  <span />

                  <p>
                    THE LONG-TERM VISION
                  </p>
                </div>

                <h2>
                  Trusted safety learning,
                  <span> wherever work happens.</span>
                </h2>

                <p>
                  By combining immersive learning,
                  digital transformation, and ethical
                  oversight, the GDILTF aims to strengthen
                  safety culture and accessibility
                  worldwide.
                </p>

                <p>
                  Through shared ownership and continuous
                  improvement, it seeks to support a
                  future where every worker—regardless
                  of geography—can access trusted,
                  effective, and adaptive safety
                  learning tools.
                </p>
              </div>

              <div className="global-movement-vision-side">
                <span>
                  GLOBAL ACCESS
                </span>

                <strong>
                  Every worker.
                  <br />
                  Every region.
                  <br />
                  Better access.
                </strong>
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            FINAL CTA
        ==================================================== */}

        <section className="global-movement-final">
          <div
            className="global-movement-final-background"
            aria-hidden="true"
          >
            <span className="global-movement-final-grid" />
            <span className="global-movement-final-glow" />
            <span className="global-movement-final-orbit" />
          </div>

          <div className="global-movement-final-container">
            <div className="global-movement-section-kicker global-movement-section-kicker-centered">
              <span />

              <p>
                GET INVOLVED
              </p>
            </div>

            <h2>
              Help shape the
              <span> global framework.</span>
            </h2>

            <p>
              Explore the proposed initiative model
              or join the stakeholder review process
              and contribute to the next phase of
              development.
            </p>

            <div className="global-movement-final-actions">
              <a
                href="/assets/GDILTF.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="global-movement-primary-link"
              >
                <span>
                  Global Initiative Model
                </span>

                <span
                  className="global-movement-button-icon"
                  aria-hidden="true"
                >
                  ↗
                </span>
              </a>

              <NavLink
                to="/contact"
                className="global-movement-outline-link"
              >
                <span>
                  Join Stakeholder Review
                </span>

                <span aria-hidden="true">
                  →
                </span>
              </NavLink>
            </div>

            <a
              href="/assets/GlobalDigitalandImmersiveTaskForceConcept.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="global-movement-document-link"
            >
              <span className="global-movement-document-icon">
                PDF
              </span>

              <span className="global-movement-document-copy">
                <small>
                  REFERENCE DOCUMENT
                </small>

                <strong>
                  Global Digital and Immersive Task
                  Force Concept
                </strong>
              </span>

              <span
                className="global-movement-document-arrow"
                aria-hidden="true"
              >
                ↗
              </span>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}